import React, { useEffect, useState } from 'react'
import {
  DragDropContext,
  Draggable,
  Droppable,
  type DropResult,
} from 'react-beautiful-dnd'
import { useTranslation } from 'react-i18next'
import { useDispatch, useSelector } from 'react-redux'
import { KibButtonNew } from '@chewy/kib-controls-react'
import { KibChevronDownIcon, KibChevronUpIcon } from '@chewy/kib-icons-react'
import { KibSkeletonText } from '@chewy/kib-skeleton-react'
import classNames from 'classnames'
import * as R from 'ramda'
import { type Nil, NumberUtils } from '@pbt/pbt-ui-components'

import {
  type GroupedLineItemArrangementInput,
  LineItemType,
  type ModifiedChargeSheetLineItemInput,
  type RetailOrderLineItem,
} from '~/api/graphql/generated/types'
import useConfirmAlert from '~/components/common/dialog/useConfirmAlert'
import {
  getInvoiceGroupSubTotal,
  getIsRetailOrderLineItem,
  isGroupedInvoiceItem,
} from '~/components/dashboard/invoices/invoiceUtils'
import { ConfirmAlertType } from '~/constants/DialogNames'
import { OrderType } from '~/constants/SOAPStates'
import {
  editRetailOrderLineItem,
  rearrangeGroupedLineItems,
  toggleInvoiceGroupExpanded,
} from '~/store/actions/finance'
import {
  deleteChargeSheetItems,
  editChargeSheetItem,
  getClientFinanceLoading,
} from '~/store/duck/clientFinanceData'
import { useIsChewyCheckoutEnabled } from '~/store/hooks/business'
import { useOpenPrescriptionDialogAfterFetchUnifiedOrder } from '~/store/hooks/orders'
import { getCurrentUserId } from '~/store/reducers/auth'
import {
  getFinanceIsFetching,
  getFinanceIsSaving,
} from '~/store/reducers/finance'
import { getOrdersIsSending } from '~/store/reducers/orders'
import { getPatientName } from '~/store/reducers/patients'
import { getClientId, getPatientId, getSoapId } from '~/store/reducers/soap'
import type { InvoiceLineItem } from '~/types'
import {
  convertInvoiceLineItemToSoapLog,
  convertRetailOrderLineItemToSoapLog,
} from '~/utils/orderUtils'

import ApplyManualDiscountModal from './ApplyManualDiscountModal'
import ChirpSoapChargesTableRow from './ChirpSoapChargesTableRow'

import '@chewy/kib-skeleton-styles/dist/styles/kib-skeleton-text.css'
import styles from './ChirpSoapChargesTable.module.scss'

export interface ChirpSoapChargesTableProps {
  chargesId: string | Nil
  editDisabled?: boolean
  isExpanded?: boolean
  items: (InvoiceLineItem | RetailOrderLineItem)[]
}

const ChirpSoapChargesTable = ({
  chargesId,
  editDisabled = false,
  isExpanded = false,
  items,
}: ChirpSoapChargesTableProps) => {
  const { t } = useTranslation(['Common', 'Payments'])
  const dispatch = useDispatch()
  const soapId = useSelector(getSoapId)
  const clientId = useSelector(getClientId)
  const patientId = useSelector(getPatientId)
  const patientName = useSelector(getPatientName(patientId))
  const modifierId = useSelector(getCurrentUserId)
  const isChewyCheckoutEnabled = useIsChewyCheckoutEnabled()
  const isFetching = useSelector(getFinanceIsFetching)
  const isFetchingChargeSheet = useSelector(getClientFinanceLoading)
  const isSaving = useSelector(getFinanceIsSaving)
  const isSendingOrders = useSelector(getOrdersIsSending)

  const [openConfirmAlert] = useConfirmAlert({
    type: ConfirmAlertType.SOAP_ORDER,
  })

  const isLoading =
    isFetching || isSaving || isSendingOrders || isFetchingChargeSheet

  const [localItems, setLocalItems] =
    useState<(InvoiceLineItem | RetailOrderLineItem)[]>(items)

  const [discountModalOpen, setDiscountModalOpen] = useState(false)
  const [selectedItemForDiscount, setSelectedItemForDiscount] = useState<
    InvoiceLineItem | RetailOrderLineItem | null
  >(null)

  // Prescription dialog hook
  const { fetchUnifiedOrderAndOpenPrescription } =
    useOpenPrescriptionDialogAfterFetchUnifiedOrder({
      isChargeSheet: true,
    })

  useEffect(() => {
    setLocalItems(items)
  }, [items])

  const handleDeleteClick = (item: InvoiceLineItem | RetailOrderLineItem) => {
    const itemName = item.name || t('Common:ITEM')

    openConfirmAlert({
      message: t('Common:YOU_WILL_REMOVE_SOMETHING', {
        something: itemName,
      }),
      onConfirm: (proceed: boolean) => {
        if (proceed && clientId && modifierId) {
          // For charge sheet line items, use deleteChargeSheetItems for ALL types
          // This matches the old table's ChargeBundleItemButtonsActions logic
          const deleteItems = [
            {
              id: item.id,
              expectedModificationDate: item.modificationDate,
            },
          ]
          dispatch(
            deleteChargeSheetItems({
              items: deleteItems,
              modifierId,
              clientId,
            }),
          )
        }
      },
    })
  }

  const handleDeclineClick = (item: InvoiceLineItem | RetailOrderLineItem) => {
    const isRetailOrder = getIsRetailOrderLineItem(item)

    if (isRetailOrder) {
      dispatch(
        editRetailOrderLineItem({
          id: item.id,
          quantity: item.quantity,
          declined: !item.declined,
          partNumber: item.partNumber,
          expectedModificationDate: item.modificationDate,
          logExpectedModificationDate: (item as RetailOrderLineItem)
            .prescription?.updatedAt,
          autoshipFrequency: (item as RetailOrderLineItem).autoshipFrequency,
          autoshipUnitId: (item as RetailOrderLineItem).autoshipUnit?.id,
        }),
      )
    } else {
      dispatch(
        editChargeSheetItem({
          updateItemInput: [
            {
              id: item.id,
              expectedModification: item.modificationDate,
              expectedSoapLogModification: (item as InvoiceLineItem)
                .soapLogModificationDate,
              declined: !item.declined,
            },
          ],
        }),
      )
    }
  }

  const handleBundleDeleteClick = (
    item: InvoiceLineItem | RetailOrderLineItem,
  ) => {
    const bundleItem = item as InvoiceLineItem

    openConfirmAlert({
      message: t('Soap:SOAP_ORDERS.CONFIRM_ALERT_MESSAGE'),
      onConfirm: (proceed: boolean) => {
        if (proceed && clientId && modifierId && bundleItem.items) {
          const deleteItems = bundleItem.items.map((childItem) => {
            const isRetailChild = getIsRetailOrderLineItem(childItem)
            return {
              id: childItem.id,
              expectedModificationDate: isRetailChild
                ? (childItem as RetailOrderLineItem).modificationDate
                : (childItem as InvoiceLineItem).modificationDate,
            }
          })
          dispatch(
            deleteChargeSheetItems({
              items: deleteItems,
              modifierId,
              clientId,
            }),
          )
        }
      },
    })
  }

  const handleBundleDeclineClick = (
    item: InvoiceLineItem | RetailOrderLineItem,
  ) => {
    const bundleItem = item as InvoiceLineItem
    if (bundleItem.items) {
      // Separate retail and invoice items
      const retailItems: RetailOrderLineItem[] = []
      const invoiceItems: InvoiceLineItem[] = []

      bundleItem.items.forEach((childItem) => {
        if (getIsRetailOrderLineItem(childItem)) {
          retailItems.push(childItem as RetailOrderLineItem)
        } else {
          invoiceItems.push(childItem as InvoiceLineItem)
        }
      })

      // Dispatch retail order updates
      retailItems.forEach((retailChild) => {
        dispatch(
          editRetailOrderLineItem({
            id: retailChild.id,
            quantity: retailChild.quantity,
            declined: !item.declined,
            partNumber: retailChild.partNumber,
            expectedModificationDate: retailChild.modificationDate,
            logExpectedModificationDate: retailChild.prescription?.updatedAt,
            autoshipFrequency: retailChild.autoshipFrequency,
            autoshipUnitId: retailChild.autoshipUnit?.id,
          }),
        )
      })

      // Dispatch invoice line item updates
      if (invoiceItems.length > 0) {
        dispatch(
          editChargeSheetItem({
            updateItemInput: invoiceItems.map((invoiceChild) => ({
              id: invoiceChild.id,
              expectedModification: invoiceChild.modificationDate,
              expectedSoapLogModification: invoiceChild.soapLogModificationDate,
              declined: !item.declined,
            })),
          }),
        )
      }
    }
  }

  // Get item ID for drag and drop - must be unique
  const getItemId = (
    item: InvoiceLineItem | RetailOrderLineItem,
    index: number,
  ) => {
    if (isGroupedInvoiceItem(item)) {
      const invoiceItem = item as InvoiceLineItem
      // Use group or fallback to id or index
      const groupKey = invoiceItem.group || invoiceItem.id || `idx-${index}`
      return `charges-table-group-${invoiceItem.soapId || 'no-soap'}_${groupKey}`
    }
    // Use id + logId, or just id, or fallback to index
    const itemKey = item.id || `idx-${index}`
    const logKey = item.logId || 'no-log'
    return `charges-table-${itemKey}_${logKey}`
  }

  // Handle drag end for reordering
  const handleDragEnd = (result: DropResult) => {
    if (!result.destination || !chargesId) return

    const from = result.source.index
    const to = result.destination.index

    if (from === to) return

    const newItemsOrder = R.move(from, to, localItems)
    const flatLocalItems = R.chain(
      (i) =>
        !getIsRetailOrderLineItem(i) && (i as InvoiceLineItem).items
          ? [...((i as InvoiceLineItem).items || [])]
          : [i],
      localItems,
    )
    const newItemsOrderSlicedFlat = R.chain(
      (i) =>
        !getIsRetailOrderLineItem(i) && (i as InvoiceLineItem).items
          ? [...((i as InvoiceLineItem).items || [])]
          : [i],
      R.slice(0, R.max(from, to) + 1, newItemsOrder),
    )

    if (isChewyCheckoutEnabled) {
      const rearrangements = newItemsOrderSlicedFlat.reduce(
        (arr: GroupedLineItemArrangementInput[], item, idx) => {
          const lineItemType = getIsRetailOrderLineItem(item)
            ? LineItemType.RetailOrderLineItem
            : LineItemType.InvoiceLineItem
          if (
            item.id &&
            (item.id !== flatLocalItems[idx]?.id ||
              item.viewOrderNumber == null)
          ) {
            arr.push({
              lineItemId: item.id,
              lineItemType,
              viewOrderNumber: idx,
            })
          }
          return arr
        },
        [],
      )
      dispatch(rearrangeGroupedLineItems(chargesId, rearrangements))
    }

    setLocalItems(newItemsOrder)
  }

  // Toggle bundle expand/collapse
  const handleToggleExpand = (item: InvoiceLineItem | RetailOrderLineItem) => {
    const groupId = getIsRetailOrderLineItem(item)
      ? null
      : (item as InvoiceLineItem).group
    dispatch(toggleInvoiceGroupExpanded(groupId || item.id, soapId))
  }

  // Handle calculator click to open discount modal
  const handleCalculatorClick = (
    item: InvoiceLineItem | RetailOrderLineItem,
  ) => {
    setSelectedItemForDiscount(item)
    setDiscountModalOpen(true)
  }

  // Handle applying discount
  const handleApplyDiscount = (
    discountAmount: number,
    quantity: number,
    discountPercentage?: number,
  ) => {
    if (!selectedItemForDiscount) {
      return
    }

    const item = selectedItemForDiscount
    const isRetailOrder = getIsRetailOrderLineItem(item)

    if (!isRetailOrder) {
      // For invoice line items, use the charge sheet item edit with discount and quantity
      const updateInput: ModifiedChargeSheetLineItemInput = {
        id: item.id,
        expectedModification: item.modificationDate,
        expectedSoapLogModification: (item as InvoiceLineItem)
          .soapLogModificationDate,
        quantity,
      }

      // Only add the discount field that's being used
      if (discountPercentage !== undefined) {
        // Percentage mode - only include discountPerc
        updateInput.discountPerc = discountPercentage
      } else {
        // Price mode - only include discountAmount
        updateInput.discountAmount = discountAmount
      }

      dispatch(
        editChargeSheetItem({
          updateItemInput: [updateInput],
        }),
      )
    }
    // Note: Retail order line items don't support discountAmount in their update input
    // Discounts for retail items are typically handled through different mechanisms

    setDiscountModalOpen(false)
    setSelectedItemForDiscount(null)
  }

  // Handle closing discount modal
  const handleCloseDiscountModal = () => {
    setDiscountModalOpen(false)
    setSelectedItemForDiscount(null)
  }

  // Separate active and declined items
  // For bundles with mixed children: split into two - one for active section, one for declined
  const { activeItems, declinedItems } = React.useMemo(() => {
    const active: (InvoiceLineItem | RetailOrderLineItem)[] = []
    const declined: (InvoiceLineItem | RetailOrderLineItem)[] = []

    localItems.forEach((item) => {
      const isBundle = isGroupedInvoiceItem(item)
      const isRetailOrder = getIsRetailOrderLineItem(item)

      if (isBundle && !isRetailOrder) {
        const bundleItem = item as InvoiceLineItem
        const children = bundleItem.items || []

        // If the bundle itself is declined, move entire bundle to declined section
        if (bundleItem.declined) {
          declined.push(bundleItem)
          return
        }

        // Separate children into active and declined
        const activeChildren = children.filter((child) => !child.declined)
        const declinedChildren = children.filter((child) => child.declined)

        // If bundle has active children, add bundle with only active children to active section
        if (activeChildren.length > 0) {
          active.push({
            ...bundleItem,
            items: activeChildren,
          })
        }

        // If bundle has declined children, add bundle with only declined children to declined section
        if (declinedChildren.length > 0) {
          declined.push({
            ...bundleItem,
            items: declinedChildren,
          })
        }
      } else if (item.declined) {
        declined.push(item)
      } else {
        active.push(item)
      }
    })

    return { activeItems: active, declinedItems: declined }
  }, [localItems])

  // State for declined section collapse
  const [declinedSectionExpanded, setDeclinedSectionExpanded] =
    useState<boolean>(false)

  // Toggle declined section
  const handleToggleDeclinedSection = () => {
    setDeclinedSectionExpanded(!declinedSectionExpanded)
  }

  // Handle prescription click to open Rx dialog
  const handlePrescriptionClick = (
    item: InvoiceLineItem | RetailOrderLineItem,
  ) => {
    // Only open prescription dialog for prescribable items
    const convertedItem = getIsRetailOrderLineItem(item)
      ? convertRetailOrderLineItemToSoapLog(item)
      : convertInvoiceLineItemToSoapLog(item)

    const { logId } = item
    const { type } = convertedItem
    if (logId) {
      fetchUnifiedOrderAndOpenPrescription(
        logId,
        getIsRetailOrderLineItem(item)
          ? OrderType.PRESCRIPTION
          : (type as OrderType),
      )
    }
  }

  // Calculate total
  const total = getInvoiceGroupSubTotal(items)
  const displayPatientName = patientName || t('Common:PET')

  // Column definitions for skeleton rendering
  const columns = [
    { key: 'item', className: styles.dataCellItem },
    { key: 'producer', className: styles.dataCellProducer },
    { key: 'quantity', className: styles.dataCellQuantity },
    { key: 'actions', className: styles.dataCellActions },
    { key: 'total', className: styles.dataCellTotal },
  ]

  // Number of skeleton rows to show while loading (including bundle children)
  const skeletonRowCount =
    localItems.reduce((count, item) => {
      const isBundle = isGroupedInvoiceItem(item)
      const isRetailOrder = getIsRetailOrderLineItem(item)
      const bundleChildCount =
        isBundle && !isRetailOrder
          ? ((item as InvoiceLineItem).items?.length ?? 0)
          : 0
      return count + 1 + bundleChildCount
    }, 0) || 4

  // Hide table when no items and not loading
  // R.isEmpty(undefined) returns false, so we need explicit check
  const hasItems = items && items.length > 0
  if (!hasItems && !isLoading) {
    return null
  }

  return (
    <div
      className={classNames(styles.tableContainer, {
        [styles.tableContainerExpanded]: isExpanded,
      })}
    >
      <div
        className={classNames(styles.tableWrapper, {
          [styles.tableWrapperExpanded]: isExpanded,
        })}
      >
        <DragDropContext onDragEnd={handleDragEnd}>
          <table className={styles.table}>
            <thead className={styles.tableHeader}>
              <tr className={styles.headerRow}>
                <th
                  className={classNames(
                    styles.headerCell,
                    styles.headerCellItem,
                  )}
                >
                  {t('Common:ITEM')}
                </th>
                <th
                  className={classNames(
                    styles.headerCell,
                    styles.headerCellProducer,
                  )}
                >
                  {t('Common:PRODUCER_FINANCIAL')}
                </th>
                <th
                  className={classNames(
                    styles.headerCell,
                    styles.headerCellQuantity,
                  )}
                >
                  {t('Common:QUANTITY')}
                </th>
                <th
                  className={classNames(
                    styles.headerCell,
                    styles.headerCellActions,
                  )}
                >
                  {t('Common:ACTION_OTHER')}
                </th>
                <th
                  className={classNames(
                    styles.headerCell,
                    styles.headerCellTotal,
                  )}
                >
                  {t('Common:TOTAL')}
                </th>
              </tr>
            </thead>
            {isLoading ? (
              <tbody data-testid="charges-table-skeleton">
                {Array.from({ length: skeletonRowCount }, (_, rowIndex) => (
                  <tr
                    className={styles.skeletonRow}
                    key={`skeleton-${rowIndex}`}
                  >
                    {columns.map((col) => (
                      <td
                        className={classNames(styles.dataCell, col.className)}
                        key={col.key}
                      >
                        <KibSkeletonText lines={1} />
                      </td>
                    ))}
                  </tr>
                ))}
              </tbody>
            ) : (
              <>
                {/* Active Items Section */}
                <Droppable droppableId="charges-table-active" type="CHARGES">
                  {(droppableProvided) => (
                    <tbody
                      ref={(ref) => {
                        droppableProvided.innerRef(ref)
                      }}
                      {...droppableProvided.droppableProps}
                    >
                      {activeItems.map((item, index) => {
                        const itemId = getItemId(item, index)
                        const isBundle = isGroupedInvoiceItem(item)
                        const isRetailOrder = getIsRetailOrderLineItem(item)
                        const bundleItems =
                          isBundle && !isRetailOrder
                            ? ((item as InvoiceLineItem).items ?? [])
                            : []

                        return (
                          <Draggable
                            draggableId={itemId}
                            index={index}
                            key={itemId}
                          >
                            {(draggableProvided, draggableSnapshot) => (
                              <ChirpSoapChargesTableRow
                                bundleItems={bundleItems}
                                dragHandleProps={
                                  draggableProvided.dragHandleProps
                                }
                                draggableProps={
                                  draggableProvided.draggableProps
                                }
                                editDisabled={editDisabled}
                                isBundle={isBundle}
                                isDragging={draggableSnapshot.isDragging}
                                item={item}
                                ref={draggableProvided.innerRef}
                                soapId={soapId}
                                onBundleDeclineClick={handleBundleDeclineClick}
                                onBundleDeleteClick={handleBundleDeleteClick}
                                onCalculatorClick={handleCalculatorClick}
                                onDeclineClick={handleDeclineClick}
                                onDeleteClick={handleDeleteClick}
                                onPrescriptionClick={handlePrescriptionClick}
                                onToggleExpand={handleToggleExpand}
                              />
                            )}
                          </Draggable>
                        )
                      })}
                      {droppableProvided.placeholder}
                    </tbody>
                  )}
                </Droppable>

                {/* Declined Items Section */}
                {declinedItems.length > 0 && (
                  <>
                    <tbody>
                      <tr className={styles.declinedHeaderRow}>
                        <td className={styles.declinedHeaderCell} colSpan={5}>
                          <KibButtonNew
                            aria-expanded={declinedSectionExpanded}
                            aria-label={
                              declinedSectionExpanded
                                ? t('Common:COLLAPSE_DECLINED_ITEMS')
                                : t('Common:EXPAND_DECLINED_ITEMS')
                            }
                            className={styles.declinedHeaderButton}
                            emphasis="tertiary"
                            icon={
                              declinedSectionExpanded ? (
                                <KibChevronUpIcon />
                              ) : (
                                <KibChevronDownIcon />
                              )
                            }
                            onClick={handleToggleDeclinedSection}
                          >
                            <span className={styles.declinedHeaderText}>
                              {t('Payments:DECLINED_BY_CLIENT_COUNT', {
                                count: declinedItems.length,
                              })}
                            </span>
                          </KibButtonNew>
                        </td>
                      </tr>
                    </tbody>

                    {declinedSectionExpanded && (
                      <tbody>
                        {declinedItems.map((item, index) => {
                          const itemId = getItemId(
                            item,
                            activeItems.length + index,
                          )
                          const isBundle = isGroupedInvoiceItem(item)
                          const isRetailOrder = getIsRetailOrderLineItem(item)
                          const bundleItems =
                            isBundle && !isRetailOrder
                              ? ((item as InvoiceLineItem).items ?? [])
                              : []

                          return (
                            <ChirpSoapChargesTableRow
                              bundleItems={bundleItems}
                              editDisabled={editDisabled}
                              isBundle={isBundle}
                              item={item}
                              key={itemId}
                              soapId={soapId}
                              onBundleDeclineClick={handleBundleDeclineClick}
                              onBundleDeleteClick={handleBundleDeleteClick}
                              onCalculatorClick={handleCalculatorClick}
                              onDeclineClick={handleDeclineClick}
                              onDeleteClick={handleDeleteClick}
                              onPrescriptionClick={handlePrescriptionClick}
                              onToggleExpand={handleToggleExpand}
                            />
                          )
                        })}
                      </tbody>
                    )}
                  </>
                )}
              </>
            )}
          </table>
        </DragDropContext>
      </div>

      {/* Footer with total */}
      <div className={styles.footer}>
        <div className={styles.footerContent}>
          <span className={styles.totalLabel}>
            {t('Common:PATIENT_TOTAL_WITH_VALUE', {
              patientName: displayPatientName,
              amount: NumberUtils.formatMoney(total),
            })}
          </span>
        </div>
      </div>

      {/* Discount Modal */}
      <ApplyManualDiscountModal
        item={selectedItemForDiscount}
        open={discountModalOpen}
        onApplyDiscount={handleApplyDiscount}
        onClose={handleCloseDiscountModal}
      />
    </div>
  )
}

export default ChirpSoapChargesTable
